import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Box,
  Tabs,
  Tab,
  Typography,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useGlossario } from '../../hooks/useGlossario';
import { TermoCard } from './TermoCard';

interface GlossarioModalProps {
  open: boolean;
  onClose: () => void;
  termoInicial?: string;
}

/**
 * Modal principal do Glossário Academy
 * 
 * Features:
 * - Busca por termo (case-insensitive)
 * - Filtro por categoria (tabs)
 * - Exibição detalhada de termos
 * - Design responsivo
 * 
 * @example
 * ```tsx
 * const { isOpen, abrir, fechar } = useGlossarioModal();
 * <GlossarioModal open={isOpen} onClose={fechar} termoInicial="WACC" />
 * ```
 */
export const GlossarioModal: React.FC<GlossarioModalProps> = ({
  open,
  onClose,
  termoInicial,
}) => {
  const {
    termosFiltrados,
    categorias,
    setCategoriaAtiva,
    termoBusca,
    setTermoBusca,
    termoSelecionado,
    selecionarTermo,
    getCategoriaName,
    buscarTermo,
  } = useGlossario();

  const [tabValue, setTabValue] = useState<number>(0);

  // Inicializar busca se termo inicial fornecido
  React.useEffect(() => {
    if (termoInicial && open) {
      setTermoBusca(termoInicial);
    }
  }, [termoInicial, open, setTermoBusca]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 0) {
      setCategoriaAtiva('todos');
    } else {
      const categoria = categorias[newValue - 1];
      setCategoriaAtiva(categoria.id);
    }
  };

  const handleReferenciaClick = (referencia: string) => {
    // Buscar o termo referenciado via fuzzy matching
    const termoReferenciado = buscarTermo(referencia);
    
    if (termoReferenciado) {
      // Limpar busca e filtro de categoria
      setTermoBusca('');
      setCategoriaAtiva('todos');
      setTabValue(0);
      // Selecionar o termo referenciado
      selecionarTermo(termoReferenciado);
      
      // Scroll suave para o topo após um pequeno delay
      setTimeout(() => {
        const dialogContent = document.querySelector('[role="dialog"] .MuiDialogContent-root');
        if (dialogContent) {
          dialogContent.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const handleClose = () => {
    setTermoBusca('');
    selecionarTermo(null);
    setTabValue(0);
    setCategoriaAtiva('todos');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '800px',
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" component="div">
            Glossário Valuation Academy
          </Typography>
          <IconButton edge="end" onClick={handleClose} aria-label="fechar">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Campo de Busca */}
        <Box sx={{ p: 2, pb: 0 }}>
          <TextField
            fullWidth
            placeholder="Buscar termo (ex: WACC, Beta, FCF...)"
            value={termoBusca}
            onChange={(e) => setTermoBusca(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
        </Box>

        {/* Tabs de Categorias */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="categorias do glossário"
          >
            <Tab label={`Todos (${termosFiltrados.length})`} />
            {categorias.map((categoria) => {
              const count = termosFiltrados.filter(t => t.categoria === categoria.id).length;
              return (
                <Tab
                  key={categoria.id}
                  label={`${categoria.nome} (${count})`}
                />
              );
            })}
          </Tabs>
        </Box>

        {/* Lista de Termos */}
        <Box sx={{ p: 2, overflow: 'auto', maxHeight: 'calc(80vh - 280px)' }}>
          {termosFiltrados.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {termoBusca
                  ? `Nenhum termo encontrado para "${termoBusca}"`
                  : 'Nenhum termo disponível nesta categoria'}
              </Typography>
            </Box>
          ) : (
            termosFiltrados.map((termo) => (
              <TermoCard
                key={termo.id}
                termo={termo}
                categoriaName={getCategoriaName(termo.categoria)}
                expanded={termoSelecionado?.id === termo.id}
                onClick={() => 
                  selecionarTermo(termoSelecionado?.id === termo.id ? null : termo)
                }
                onReferenciaClick={handleReferenciaClick}
              />
            ))
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
